import React, { useState, useEffect, useRef, useMemo, type ReactNode } from "react";
import styles from "~/widgets/SearchModal/SearchModal.module.scss";

export interface FooterHint {
  label: string;
  hint: string;
}

export interface SearchModalProps<T> {
  isOpen: boolean;
  onClose: () => void;
  placeholder?: string;
  items: T[];
  getItemKey: (item: T) => string;
  filterFn: (item: T, query: string) => boolean;
  renderItem: (item: T, isSelected: boolean) => ReactNode;
  onSelect: (item: T) => void;
  emptyMessage?: string;
  emptyHint?: string;
  footerHints?: FooterHint[];
  /** Called when Enter is pressed with no items or query-only mode */
  onEmptySubmit?: (query: string) => void;
}

/**
 * SearchModal Component
 *
 * A generic, reusable search modal with keyboard navigation.
 * Used as a base for RollSearch, CharacterSearch, and CommandPalette.
 *
 * Features:
 * - Overlay with backdrop blur and click-to-close
 * - Search input with ESC hint
 * - Results list with keyboard navigation (up/down/Enter/Esc)
 * - Selected item highlighting with mouse hover sync
 * - Dark mode support
 * - Animations (zoom-in on open)
 * - Auto-focus on open, reset query on open
 */
function SearchModal<T>({
  isOpen,
  onClose,
  placeholder = "Search...",
  items,
  getItemKey,
  filterFn,
  renderItem,
  onSelect,
  emptyMessage = "No results found",
  emptyHint,
  footerHints = [
    { label: "navigate", hint: "↑↓" },
    { label: "select", hint: "↵" },
  ],
  onEmptySubmit,
}: SearchModalProps<T>): React.ReactElement | null {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter items based on query
  const filteredItems = useMemo(() => {
    if (!query.trim()) return items;
    return items.filter((item) => filterFn(item, query.toLowerCase()));
  }, [query, items, filterFn]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems.length]);

  const handleSelect = (item?: T) => {
    if (item) {
      onSelect(item);
      onClose();
    } else if (filteredItems.length > 0) {
      onSelect(filteredItems[selectedIndex]);
      onClose();
    } else if (onEmptySubmit) {
      onEmptySubmit(query);
      onClose();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        filteredItems.length > 0
          ? (prev + 1) % filteredItems.length
          : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        filteredItems.length > 0
          ? (prev - 1 + filteredItems.length) % filteredItems.length
          : 0
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.searchContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            type="text"
            className={styles.searchInput}
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
          />
          <kbd className={styles.escHint}>ESC</kbd>
        </div>

        <ul className={styles.resultsList}>
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <li
                key={getItemKey(item)}
                className={`${styles.resultItem} ${index === selectedIndex ? styles.selected : ""}`}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {renderItem(item, index === selectedIndex)}
              </li>
            ))
          ) : (
            <li className={styles.noResults}>
              <div className={styles.noResultsTitle}>{emptyMessage}</div>
              {emptyHint && (
                <div className={styles.noResultsHint}>{emptyHint}</div>
              )}
            </li>
          )}
        </ul>

        <div className={styles.footer}>
          {footerHints.map((hint) => (
            <span key={hint.label}>
              {hint.hint} {hint.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SearchModal;
